import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Text from "../Text";
import Img from "../Img";
import imgLeft from '../../assets/img/img_left.svg'; // or your uploaded room image
import imgRight from '../../assets/img/img_right.svg'; // or your uploaded room image
import { useTranslation } from "react-i18next";

interface BarnCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  barns: any[]; // Replace 'any' with a more specific type if available
}

function BarnCarousel({ barns, ...props }: BarnCarouselProps) {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  
  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % barns.length);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + barns.length) % barns.length);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  // Auto-scroll every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full flex flex-col items-center" {...props}>
      <Text
        size="text8xl"
        as="p"
        className="text-[40px] font-normal !text-white-a700 lg:text-[34px] md:text-[34px] sm:text-[32px] mb-4"
        style={{ fontSize: '3em' }}
      >
        {t("home.carousel.animal.title")}
      </Text>
      
      <Text
        as="p"
        className="text-[20px] font-normal !text-white-a700 text-center max-w-4xl mb-32 px-4 font-upheaval barn-description"
        style={{ fontSize: '1.5em', marginBottom: '100px' }}

      >
        {t("home.carousel.animal.subtitle_p1")}<span className="highlight">{t("home.carousel.animal.subtitle_p2")}</span>
      </Text>
      
      <div className="relative w-full max-w-6xl flex flex-col justify-center items-center pt-10">
        {/* Carousel main container */}
        <div className="w-full flex items-center justify-center px-4 relative">
          {/* Left Arrow */}
          <button 
            onClick={prevSlide} 
            className="absolute left-0 z-30 p-2 cursor-pointer transform -translate-x-1/2"
            aria-label="Previous barn"
            style={{marginLeft: '24px'}}
          >
            <Img 
              src={imgLeft} 
              alt="Previous" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
          
          {/* Main card - always centered and prominent */}
          <div className="flex justify-center items-center gap-10 px-4 overflow-visible w-full relative" style={{gap: '3rem'}}>
            {[-1, 0, 1].map((offset) => {
              const index = (currentIndex + offset + barns.length) % barns.length;
              const isMain = offset === 0;
              
              // Different styling for main card vs side cards
              const cardClass = isMain 
                ? "z-20 transform scale-125 w-[320px]" 
                : offset < 0 
                  ? "z-10 opacity-60 -mr-16 w-[260px]" 
                  : "z-10 opacity-60 -ml-16 w-[260px]";
              
              return (
                <div 
                  key={index}
                  className={`transition-all duration-300 ${cardClass}`}
                >
                  <div className={`barn-card-container ${isMain ? 'main-card' : 'side-card'}`}>
                    <div className="pixel-barn-container">
                      <div className="pixel-barn-box">
                        <Img 
                          src={barns[index].image} 
                          alt={barns[index].name}
                          style={{ width: 'auto', height: '200px' }}
                          className="w-auto object-contain mb-3" 
                        />
                        <Text 
                          as="p" 
                          className="text-[28px] font-normal font-upheaval !text-white-a700 text-center"
                          style={{ fontSize: '2em' }}
                        >
                          {barns[index].name}
                        </Text>
                        
                        {/* Barn stats */}
                        <div className="flex flex-col w-full mt-3 items-center gap-1">
                          <Text 
                            as="p" 
                            className="text-[18px] font-normal font-upheaval !text-white-a700 text-center"
                            style={{ fontSize: '1.3em' }}

                       >
                            MAX ITEMS: {barns[index].maxMiners}
                          </Text>
                          <Text 
                            as="p" 
                            className="text-[18px] font-normal font-upheaval !text-white-a700 text-center"
                            style={{ fontSize: '1.1em' }}

                          >
                            FOOD AVAILABLE: {barns[index].power}
                          </Text>
                        </div>
                      </div>
                      <div className="pixel-scanlines"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Right Arrow */}
          <button 
            onClick={nextSlide} 
            className="absolute right-0 z-30 p-2 cursor-pointer transform translate-x-1/2"
            aria-label="Next barn"
          >
            <Img 
              src={imgRight} 
              alt="Next" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
        </div>
        
        {/* Navigation dots */}
        <div className="flex justify-center gap-3 mt-16">
          {barns.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsAnimating(true);
                setTimeout(() => setIsAnimating(false), 300);
              }}
              className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 ${
                index === currentIndex ? "bg-[#507c83]" : "bg-gray-600 opacity-40"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

BarnCarousel.propTypes = {
  barns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      maxMiners: PropTypes.string.isRequired,
      power: PropTypes.string.isRequired
    })
  ).isRequired
};

export { BarnCarousel };